import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import Anchor from 'components/Anchor';
import CardWrapper from 'components/Timeline/OfferCard/V2/OfferGridCard/CardWrapper';

import useExclusiveOffer from 'hooks/useExclusiveOffer';

import { sendEvent } from 'lib/gtag';
import { toOfferLink } from 'lib/links';
import trackEvent from 'lib/posthog/trackEvent';
import { toFormattedGaEventActionPrefix } from 'lib/string';
import { noop } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import CardContent from './CardContent';
import WhatsAppShareIconButton from './WhatsAppShareIconButton';

import OFFER from 'constants/offer';

const HomeOfferCard = ({
  authors = [],
  cardTitleAs = 'span',
  isOffersShowcaseCard = false,
  offerComments,
  offerImagePriority = false,
  offerLikes,
  offerOldPrice = null,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerPublished,
  offerStatusName,
  offerTags = [],
  offerTitle,
  offerCoupon = null,
  storeDomain,
  userId,
  userName,
  userPhoto,
  gaEventActionPrefix = null,
  gaEventCategory = null,
  offerId,
  offerSlug,
  offerUserVisibility = OFFER.VISIBILITY_STATUS.ALL,
  onImageClick = noop,
  onTitleClick = noop,
  className,
  isCarousel = false,
  surface = null,
}) => {
  const {
    isOfferBlocked,
    formatGaEventActionByVisibility,
    onUnloggedUserTriesOpenExclusiveOffer,
  } = useExclusiveOffer();
  const { isLoggedIn } = useUser();

  const onClick = (event) => {
    sendEvent({
      action: formatGaEventActionByVisibility(
        'click__offer',
        offerUserVisibility
      ),
      category: gaEventCategory,
      logged: isLoggedIn,
    });
    trackEvent(
      'click home offer card',
      { offer_id: offerId, store_slug: storeDomain, surface },
      { destination: 'posthog' }
    );

    if (!isOfferBlocked(offerUserVisibility, offerSlug)) {
      return;
    }

    event.preventDefault();
    onUnloggedUserTriesOpenExclusiveOffer(offerSlug, offerUserVisibility);
  };

  const onImageClickHandler = useCallback(
    (e) => {
      if (gaEventCategory) {
        sendEvent({
          category: gaEventCategory,
          action: `${toFormattedGaEventActionPrefix(
            gaEventActionPrefix
          )}product_image`,
          label: offerId,
        });
      }

      onImageClick(e);
    },
    [gaEventCategory, gaEventActionPrefix]
  );

  const onTitleClickHandler = useCallback(
    (e) => {
      if (gaEventCategory) {
        sendEvent({
          category: gaEventCategory,
          action: `${toFormattedGaEventActionPrefix(
            gaEventActionPrefix
          )}product_title`,
          label: offerId,
        });
      }

      onTitleClick(e);
    },
    [gaEventCategory, gaEventActionPrefix]
  );

  const isOfferExclusiveForApp =
    offerUserVisibility === OFFER.VISIBILITY_STATUS.APP_ONLY;
  const isOfferExclusiveForLoggedUsers =
    offerUserVisibility === OFFER.VISIBILITY_STATUS.LOGGED_ONLY;

  if (isOffersShowcaseCard) {
    return (
      <CardWrapper
        className={className}
        gaEventCategory={gaEventCategory}
        offerCoupon={offerCoupon}
        offerId={offerId}
        offerSlug={offerSlug}
        offerUserVisibility={offerUserVisibility}
      >
        <CardContent
          authors={authors}
          cardTitleAs={cardTitleAs}
          gaEventCategory={gaEventCategory}
          isOfferExclusiveForApp={isOfferExclusiveForApp}
          isOfferExclusiveForLoggedUsers={isOfferExclusiveForLoggedUsers}
          isOffersShowcaseCard={isOffersShowcaseCard}
          offerComments={offerComments}
          offerId={offerId}
          offerImagePriority={offerImagePriority}
          offerLikes={offerLikes}
          offerOldPrice={offerOldPrice}
          offerPhoto={offerPhoto}
          offerPrice={offerPrice}
          offerPriceType={offerPriceType}
          offerPublished={offerPublished}
          offerSlug={offerSlug}
          offerStatusName={offerStatusName}
          offerTags={offerTags}
          offerTitle={offerTitle}
          offerUserVisibility={offerUserVisibility}
          storeDomain={storeDomain}
          userId={userId}
          userName={userName}
          userPhoto={userPhoto}
          onImageClickHandler={onImageClickHandler}
          onTitleClickHandler={onTitleClickHandler}
        />
      </CardWrapper>
    );
  }

  return (
    <NextLink href={toOfferLink(offerSlug)} prefetch={false} passHref>
      <Anchor
        className={twMerge(
          'relative flex select-none flex-col rounded-4 border border-neutral-high-200 bg-neutral-high-100 p-4 dark:border-neutral-low-400 dark:bg-neutral-low-400 lg:p-6 lg:pb-3',
          isCarousel ? null : 'h-full justify-between',
          className
        )}
        onClick={onClick}
      >
        <div
          className={`absolute right-6 top-4 z-10 ${isCarousel ? 'flex' : 'hidden lg:flex'} flex-col gap-2`}
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* <AlertIconButton
            gaEventCategory={gaEventCategory}
            offerStatusName={offerStatusName}
          /> */}
          <WhatsAppShareIconButton
            offerId={offerId}
            offerPrice={offerPrice}
            offerPriceType={offerPriceType}
            offerSlug={offerSlug}
            offerTitle={offerTitle}
          />
        </div>
        <CardContent
          authors={authors}
          cardTitleAs={cardTitleAs}
          gaEventCategory={gaEventCategory}
          isCarousel={isCarousel}
          isOfferExclusiveForApp={isOfferExclusiveForApp}
          isOfferExclusiveForLoggedUsers={isOfferExclusiveForLoggedUsers}
          isOffersShowcaseCard={isOffersShowcaseCard}
          offerComments={offerComments}
          offerCoupon={offerCoupon}
          offerId={offerId}
          offerImagePriority={offerImagePriority}
          offerLikes={offerLikes}
          offerOldPrice={offerOldPrice}
          offerPhoto={offerPhoto}
          offerPrice={offerPrice}
          offerPriceType={offerPriceType}
          offerPublished={offerPublished}
          offerSlug={offerSlug}
          offerStatusName={offerStatusName}
          offerTags={offerTags}
          offerTitle={offerTitle}
          offerUserVisibility={offerUserVisibility}
          storeDomain={storeDomain}
          userId={userId}
          userName={userName}
          userPhoto={userPhoto}
          onImageClickHandler={onImageClickHandler}
          onTitleClickHandler={onTitleClickHandler}
        />
      </Anchor>
    </NextLink>
  );
};

HomeOfferCard.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      typeName: PropTypes.string.isRequired,
      specialityName: PropTypes.string,
      level: PropTypes.number.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
  cardTitleAs: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  gaEventActionPrefix: PropTypes.string,
  isOffersShowcaseCard: PropTypes.bool,
  offerId: PropTypes.number.isRequired,
  offerLikes: PropTypes.number.isRequired,
  offerComments: PropTypes.number.isRequired,
  offerIsHighlight: PropTypes.bool,
  offerImagePriority: PropTypes.bool,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerPublished: PropTypes.string.isRequired,
  offerSlug: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
  offerCoupon: PropTypes.string,
  offerTitle: PropTypes.string.isRequired,
  storeDomain: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string,
  userUsername: PropTypes.string,
  onImageClick: PropTypes.func,
  onTitleClick: PropTypes.func,
};

export default HomeOfferCard;
